import { useRef, useState } from 'react';
import Modal from '../../../components/common/Modal';
import Field from '../../../components/common/Field';
import CropPhotoModal from '../../../components/common/CropPhotoModal';
import { todayISO } from '../../../utils/formatters';

export const GENDERS = [
  { value: 'boy', label: 'Boy', emoji: '👦' },
  { value: 'girl', label: 'Girl', emoji: '👧' },
  { value: 'other', label: 'Other', emoji: '👀' },
];

const EMPTY = { name: '', gender: '', bday: '', file: null, preview: '' };

export default function AddChildModal({ open, onClose, onAdd }) {
  const [form, setForm] = useState(EMPTY);
  const [cropFile, setCropFile] = useState(null); // picked file awaiting the cropper
  const fileRef = useRef(null);

  const close = () => {
    if (form.preview) URL.revokeObjectURL(form.preview);
    setForm(EMPTY);
    setCropFile(null);
    onClose();
  };

  /* the picked file goes through the cropper; only the cropped square is kept */
  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) setCropFile(file);
  };
  const usePhoto = (cropped) => {
    if (form.preview) URL.revokeObjectURL(form.preview);
    setForm((f) => ({ ...f, file: cropped, preview: URL.createObjectURL(cropped) }));
    setCropFile(null);
  };

  /* Native date input, deliberately WITHOUT the calendar page's .date-input-wrap:
     that wrapper stretches the invisible picker button over the whole field, so
     every click opened the picker and nothing could be typed (the client's
     complaint). Bare, the browser puts its own calendar icon at the end and the
     month/day/year segments take keyboard input — both ways work. `max` caps the
     picker at today; a typed future date is caught here. */
  const bdayProblem = form.bday && form.bday > todayISO() ? "Birth date can't be in the future." : '';
  const complete = form.name.trim() && form.gender && form.bday && !bdayProblem;

  const add = () => {
    if (!complete) return;
    const id = `${Date.now()}`;
    /* imagePath doubles as the upload's filename — that pairing is how the API
       matches each childProfilePic back to its child, so it has to be unique. */
    const ext = (form.file?.name.split('.').pop() || 'jpg').toLowerCase();
    onAdd({
      id,
      childName: form.name.trim(),
      gender: form.gender,
      birthDate: new Date(`${form.bday}T00:00:00`).toISOString(),
      imagePath: form.file ? `child-${id}.${ext}` : '',
      file: form.file,
      preview: form.preview,
    });
    /* the preview now belongs to the kid row, so don't revoke it here */
    setForm(EMPTY);
    onClose();
  };

  const selected = GENDERS.find((g) => g.value === form.gender);

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title="Add a Child"
        footer={
          <>
            <button className="btn btn-ghost" onClick={close}>Cancel</button>
            <button className="btn btn-primary" disabled={!complete} onClick={add}>Add Child</button>
          </>
        }
      >
        <p className="ob-modal-intro">Tell us a little about your kid so we can find the right activities.</p>

        <div className="ob-photo-block">
          <button type="button" className="ob-photo ob-photo-sm" onClick={() => fileRef.current?.click()}>
            {form.preview ? <img src={form.preview} alt="" /> : <span className="em">{selected?.emoji || '👦'}</span>}
            <span className="ob-photo-badge plus">+</span>
          </button>
          <button type="button" className="ob-photo-label muted" onClick={() => fileRef.current?.click()}>
            Add a photo (optional)
          </button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickPhoto} />
        </div>

        <div className="form-stack">
          <Field label="Child's Name">
            <input
              type="text"
              value={form.name}
              placeholder="e.g. Mia"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>

          <Field label="Gender">
            <div className="ob-choice-row">
              {GENDERS.map((g) => (
                <button
                  key={g.value}
                  type="button"
                  className={`ob-choice ${form.gender === g.value ? 'on' : ''}`}
                  onClick={() => setForm({ ...form, gender: g.value })}
                >
                  <span className="em">{g.emoji}</span>
                  {g.label}
                </button>
              ))}
            </div>
          </Field>

          <Field
            label="Birth Date"
            className={bdayProblem ? 'missing' : ''}
            hint={
              bdayProblem
                ? <span style={{ color: 'var(--red)' }}>{bdayProblem}</span>
                : 'Used to suggest age-right activities — nothing else.'
            }
          >
            <input
              type="date"
              value={form.bday}
              max={todayISO()}
              onChange={(e) => setForm({ ...form, bday: e.target.value })}
            />
          </Field>
        </div>
      </Modal>

      {/* its own portal, rendered after this modal's, so it stacks on top */}
      <CropPhotoModal file={cropFile} onCancel={() => setCropFile(null)} onDone={usePhoto} />
    </>
  );
}
